import {
  IMPERSONATION_ENTITLEMENTS,
  PORTAL_ACCESS_ENTITLEMENTS,
  REQUIRED_IDENTITY_CONFIGURATION,
  SAML_ATTRIBUTE_NAMES,
  SHIBBOLETH_PROVIDERS_CONFIGURATION,
} from '../constants';

/**
 * Explain common sign-in failures without exposing SDK implementation details.
 */
export const getFriendlyAuthError = (error) => {
  if (error?.code === 'auth/popup-closed-by-user') {
    return 'Sign-in was canceled. Select Sign in to try again.';
  }

  if (error?.code === 'auth/popup-blocked') {
    return 'Your browser blocked the sign-in window. Allow popups for this site and try again.';
  }

  if (error?.code === 'auth/unauthorized-domain') {
    return 'This site is not authorized in Identity Platform. Contact the application administrator.';
  }

  return error?.message ?? 'Unable to sign in. Please try again.';
};

/**
 * Parse the provider choices generated by Terraform for this environment.
 *
 * Tests may pass a configuration string directly so they do not depend on a
 * developer's ignored frontend/.env file.
 */
export const getShibbolethProviders = (
  providersConfiguration = SHIBBOLETH_PROVIDERS_CONFIGURATION,
) => {
  let providers;

  try {
    providers = JSON.parse(providersConfiguration);
  } catch {
    throw new Error('REACT_APP_SHIBBOLETH_PROVIDERS must contain valid JSON.');
  }

  if (!Array.isArray(providers) || !providers.length) {
    throw new Error('At least one Shibboleth provider must be configured.');
  }

  return providers;
};

/**
 * Return a configured provider or fail before calling the Firebase Auth SDK.
 */
export const getShibbolethProvider = (
  providerId,
  providersConfiguration = SHIBBOLETH_PROVIDERS_CONFIGURATION,
) => {
  const provider = getShibbolethProviders(providersConfiguration).find(
    (candidate) => candidate.providerId === providerId,
  );

  if (!provider) {
    throw new Error('The selected Shibboleth provider is not configured.');
  }

  return provider;
};

/**
 * Fail early with the exact names of missing or malformed build-time settings.
 */
export const validateIdentityConfiguration = () => {
  const missingNames = Object.entries(REQUIRED_IDENTITY_CONFIGURATION)
    .filter(([, value]) => !value)
    .map(([name]) => name);

  if (missingNames.length) {
    throw new Error(`Missing Shibboleth configuration: ${missingNames.join(', ')}`);
  }

  // Parsing here reports malformed provider configuration before opening a
  // sign-in popup.
  getShibbolethProviders();
};

/**
 * Normalize a SAML attribute to an array because Identity Platform may return a
 * single string or an array, depending on how many values the IdP released.
 */
export const toStringList = (value) => {
  const values = Array.isArray(value) ? value : [value];
  return values
    .filter((item) => typeof item === 'string')
    .map((item) => item.trim())
    .filter(Boolean);
};

/**
 * Read the comma-separated authorization setting embedded during the build.
 */
export const parseConfiguredEntitlements = (value) =>
  typeof value === 'string' ? toStringList(value.split(',')) : [];

/**
 * Compare exact entitlement names. Partial matches would be unsafe because
 * similarly named directory groups can have different permissions.
 */
export const hasAnyEntitlement = (userEntitlements, allowedEntitlements) => {
  const userEntitlementSet = new Set(toStringList(userEntitlements));
  return toStringList(allowedEntitlements).some((item) => userEntitlementSet.has(item));
};

/**
 * Build the small, serializable user profile that the UI needs.
 *
 * The ID token itself remains owned by the Firebase Auth SDK. Redux receives
 * only selected profile attributes and the derived impersonation permission.
 */
export const getIdentityUser = async (
  firebaseUser,
  impersonationEntitlementsConfiguration = IMPERSONATION_ENTITLEMENTS,
  portalAccessEntitlementsConfiguration = PORTAL_ACCESS_ENTITLEMENTS,
) => {
  const tokenResult = await firebaseUser.getIdTokenResult();
  const attributes = tokenResult.claims?.firebase?.sign_in_attributes ?? {};
  const entitlements = toStringList(attributes[SAML_ATTRIBUTE_NAMES.ENTITLEMENTS]);
  const impersonationEntitlements = parseConfiguredEntitlements(
    impersonationEntitlementsConfiguration,
  );
  const portalAccessEntitlements = parseConfiguredEntitlements(
    portalAccessEntitlementsConfiguration,
  );

  return {
    uid: firebaseUser.uid,
    displayName: attributes[SAML_ATTRIBUTE_NAMES.DISPLAY_NAME] ?? firebaseUser.displayName ?? null,
    email: attributes[SAML_ATTRIBUTE_NAMES.EMAIL] ?? firebaseUser.email ?? null,
    givenName: attributes[SAML_ATTRIBUTE_NAMES.GIVEN_NAME] ?? null,
    familyName: attributes[SAML_ATTRIBUTE_NAMES.FAMILY_NAME] ?? null,
    roles: toStringList(attributes[SAML_ATTRIBUTE_NAMES.ROLES]),
    entitlements,
    canAccessPortal: hasAnyEntitlement(entitlements, portalAccessEntitlements),
    canImpersonate: hasAnyEntitlement(entitlements, impersonationEntitlements),
  };
};
